// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { Component, OnInit } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import {
  GameLevelResourceExerciseBankItem,
  GameLevelResourceService,
} from '../../../../../../../services/game-level-resource.service';
import { GameLevelItem, GameLevelService } from '../../../../../../../services/game-level.service';
import { SyllabusService } from '../../../../../../../services/syllabus.service';

type SyllabusNode = { id: number; name: string };

@Component({
    selector: 'app-game-level-exercise-bank-index',
    templateUrl: './game-level-exercise-bank-index.component.html',
    styleUrls: ['./game-level-exercise-bank-index.component.scss'],
    standalone: false
})
export class GameLevelExerciseBankIndexComponent implements OnInit {
  loading = false;
  filtersLoading = false;
  rows: GameLevelResourceExerciseBankItem[] = [];
  total = 0;
  pageIndex = 1;
  pageSize = 10;

  gameLevels: GameLevelItem[] = [];
  filterLevelId: number | null = null;

  /** 两级级联：与练习管理列表同源 category 一次取树，仅保留上两级，第二级为叶子；必选叶子后才可选习题包 */
  pathCascaderOptions: Array<{
    id: number;
    name: string;
    children: Array<SyllabusNode & { isLeaf?: boolean }>;
  }> = [];
  pathCascaderValues: number[] | null = null;

  exerciseOptions: SyllabusNode[] = [];
  filterExerciseId: number | null = null;

  /** 当前页勾选（id 为 game_level_resource.id） */
  setOfCheckedId = new Set<number>();
  checked = false;
  indeterminate = false;

  constructor(
    private resourceSvc: GameLevelResourceService,
    private gameLevelSvc: GameLevelService,
    private syllabusSvc: SyllabusService,
    private msg: NzMessageService,
    private modal: NzModalService,
  ) {}

  ngOnInit(): void {
    this.initFilterOptions().then(() => this.load());
  }

  initFilterOptions(): Promise<void> {
    this.filtersLoading = true;
    return Promise.all([this.gameLevelSvc.list().then((list) => {
      this.gameLevels = Array.isArray(list) ? list : [];
    }), this.buildPathCascaderOptions()])
      .then(() => {
        this.filtersLoading = false;
      })
      .catch((err: any) => {
        this.filtersLoading = false;
        this.msg.error(err?.message || '筛选选项加载失败');
      });
  }

  private async buildPathCascaderOptions(): Promise<void> {
    this.pathCascaderOptions = await this.syllabusSvc.getExercisePathCascaderOptionsTwoLevels();
  }

  onPathCascaderChange(vals: number[] | null): void {
    this.filterExerciseId = null;
    this.exerciseOptions = [];
    if (Array.isArray(vals) && vals.length >= 2) {
      const leafId = vals[vals.length - 1];
      this.syllabusSvc.getExercises(leafId).then((list: SyllabusNode[]) => {
        this.exerciseOptions = Array.isArray(list) ? list : [];
      });
    }
  }

  search(): void {
    this.pageIndex = 1;
    this.setOfCheckedId.clear();
    this.refreshCheckedStatus();
    this.load();
  }

  /** 与练习管理列表一致：表头右侧「搜索」触发 */
  doFilter(_evt: unknown): void {
    this.search();
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const nextPageIndex = +(params.pageIndex ?? 1);
    const nextPageSize = +(params.pageSize ?? 10);
    if (nextPageIndex === this.pageIndex && nextPageSize === this.pageSize) {
      return;
    }
    this.pageIndex = nextPageIndex;
    this.pageSize = nextPageSize;
    this.setOfCheckedId.clear();
    this.refreshCheckedStatus();
    this.load();
  }

  updateCheckedSet(id: number, checked: boolean): void {
    if (checked) {
      this.setOfCheckedId.add(id);
    } else {
      this.setOfCheckedId.delete(id);
    }
  }

  refreshCheckedStatus(): void {
    const idsOnPage = this.rows.map((r) => r.id);
    this.checked = idsOnPage.length > 0 && idsOnPage.every((id) => this.setOfCheckedId.has(id));
    this.indeterminate =
      idsOnPage.some((id) => this.setOfCheckedId.has(id)) && !this.checked;
  }

  onAllChecked(checked: boolean): void {
    this.rows.forEach((row) => this.updateCheckedSet(row.id, checked));
    this.refreshCheckedStatus();
  }

  onItemChecked(id: number, checked: boolean): void {
    this.updateCheckedSet(id, checked);
    this.refreshCheckedStatus();
  }

  /** 批量删除：POST .../batch-delete，body 为 game_level_resource.id 列表（与文档一致，勿与 game_level.id 混淆） */
  confirmBatchDelete(): void {
    const ids = Array.from(this.setOfCheckedId);
    if (!ids.length) {
      this.msg.warning('请先勾选要删除的项');
      return;
    }
    const n = ids.length;
    this.modal.confirm({
      nzTitle: '确认批量删除',
      nzContent: `确定删除选中的 ${n} 条题库关联吗？删除后不可恢复。`,
      nzOnOk: () => this.executeBatchDelete(ids),
    });
  }

  confirmDeleteOne(row: GameLevelResourceExerciseBankItem): void {
    this.modal.confirm({
      nzTitle: '确认删除',
      nzContent: '确定删除该条题库关联吗？删除后不可恢复。',
      nzOnOk: () => this.executeBatchDelete([row.id]),
    });
  }

  /** ids 为 game_level_resource 主键，须唯一、至少 1 个 */
  private executeBatchDelete(ids: number[]): Promise<void> {
    const unique = Array.from(new Set(ids)).filter((id) => id >= 1);
    if (!unique.length) {
      return Promise.resolve();
    }
    return this.resourceSvc
      .batchDeleteByResourceIds({ ids: unique })
      .then((res) => {
        this.msg.success(`已删除 ${res.deletedCount} 条`);
        this.setOfCheckedId.clear();
        this.refreshCheckedStatus();
        this.load();
      })
      .catch((err: any) => {
        this.msg.error(err?.message || '删除失败');
      });
  }

  resetFilters(): void {
    this.filterLevelId = null;
    this.pathCascaderValues = null;
    this.filterExerciseId = null;
    this.exerciseOptions = [];
    this.pageIndex = 1;
    this.setOfCheckedId.clear();
    this.refreshCheckedStatus();
    this.load();
  }

  private pathLeafId(): number | null {
    const v = this.pathCascaderValues;
    if (!Array.isArray(v) || v.length < 2) {
      return null;
    }
    return v[v.length - 1];
  }

  private buildQuery(): Record<string, number> {
    const q: Record<string, number> = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
    };
    if (this.filterLevelId != null && this.filterLevelId > 0) {
      q.levelId = this.filterLevelId;
    }
    const leaf = this.pathLeafId();
    if (leaf != null && leaf > 0) {
      q.rltSid = leaf;
    }
    if (this.filterExerciseId != null && this.filterExerciseId > 0) {
      q.exerciseId = this.filterExerciseId;
    }
    return q;
  }

  load(): void {
    this.loading = true;
    this.resourceSvc
      .pageExerciseBank(this.buildQuery())
      .then((res) => {
        this.rows = Array.isArray(res?.rows) ? res.rows : [];
        this.total = typeof res?.total === 'number' ? res.total : 0;
        if (typeof res?.pageIndex === 'number') {
          this.pageIndex = res.pageIndex;
        }
        if (typeof res?.pageSize === 'number') {
          this.pageSize = res.pageSize;
        }
        this.loading = false;
        this.refreshCheckedStatus();
      })
      .catch(() => {
        this.loading = false;
      });
  }

  trackById(_: number, row: GameLevelResourceExerciseBankItem): number {
    return row.id;
  }

  displayText(v: string | null | undefined): string {
    if (v === null || v === undefined || v === '') {
      return '—';
    }
    return v;
  }
}
